'use client';

import * as React from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import styles from './GlobalBanner.module.css';

export interface GlobalBannerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  children?: React.ReactNode;
  variant?: 'success' | 'info' | 'warning' | 'error';
  icon?: React.ReactNode;
  action?: React.ReactNode;
  dismissible?: boolean;
  dismissLabel?: string;
  autoHideDuration?: number;
  onDismiss?: () => void;
}

const defaultIcons: Record<NonNullable<GlobalBannerProps['variant']>, React.ReactNode> = {
  success: '🎉',
  info: '🚀',
  warning: '⚠️',
  error: '🔴',
};

export function GlobalBanner({
  title,
  children,
  variant = 'info',
  icon,
  action,
  dismissible = true,
  dismissLabel = 'Dismiss banner',
  autoHideDuration,
  onDismiss,
  className,
  ...props
}: GlobalBannerProps) {
  const [visible, setVisible] = React.useState(true);

  const dismiss = React.useCallback(() => {
    setVisible(false);
    onDismiss?.();
  }, [onDismiss]);

  React.useEffect(() => {
    if (!autoHideDuration) return;

    const timer = window.setTimeout(dismiss, autoHideDuration);
    return () => window.clearTimeout(timer);
  }, [autoHideDuration, dismiss]);

  if (!visible) return null;

  return (
    <div className={styles.container}>
      <div
        className={clsx(styles.banner, className)}
        data-variant={variant}
        role={variant === 'error' ? 'alert' : 'status'}
        {...props}
      >
        <span className={styles.message}>
          <span className={styles.icon} aria-hidden="true">
            {icon ?? defaultIcons[variant]}
          </span>
          <span className={styles.content}>
            <strong>{title}</strong>
            {children ? <> {children}</> : null}
          </span>
        </span>
        {action || dismissible ? (
          <span className={styles.actions}>
            {action}
            {dismissible ? (
              <button
                type="button"
                className={styles.dismiss}
                aria-label={dismissLabel}
                onClick={dismiss}
              >
                <X size={18} aria-hidden="true" />
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default GlobalBanner;
